import {useEffect, useId} from 'react';
import {useForm} from 'react-hook-form';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {Form} from '@ui/forms/form';
import {FormSwitch} from '@ui/forms/toggle/switch';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {User} from '@ui/types/user';
import {useNotificationSubscriptions} from '@common/notifications/subscriptions/requests/notification-subscriptions';
import {useUpdateNotificationSettings} from '@common/notifications/subscriptions/requests/update-notification-settings';

interface Props {
  user: User;
}

interface FormValue {
  database: boolean;
  email: boolean;
}

const RELEASE_NOTIF_ID = 'T01';

export function ReleaseNotificationSettingsPanel({user}: Props) {
  const formId = useId();
  const form = useForm<FormValue>({
    defaultValues: {
      database: true,
      email: false,
    },
  });

  const query = useNotificationSubscriptions();
  const updateSettings = useUpdateNotificationSettings();

  useEffect(() => {
    const selection = query.data?.user_selections.find(
      item => item.notif_id === RELEASE_NOTIF_ID,
    );

    form.reset({
      database: selection?.channels?.database ?? true,
      email: selection?.channels?.email ?? false,
    });
  }, [query.data?.user_selections, form]);

  return (
    <AccountSettingsPanel
      id="release-notifications"
      title={<Trans message="Release notifications" />}
      actions={
        <Button
          type="submit"
          variant="flat"
          color="primary"
          form={formId}
          disabled={updateSettings.isPending}
        >
          <Trans message="Save" />
        </Button>
      }
    >
      {query.isLoading ? (
        <ProgressCircle
          className="my-20"
          aria-label="Loading notification settings..."
          isIndeterminate
        />
      ) : (
        <Form
          form={form}
          id={formId}
          onSubmit={values => {
            updateSettings.mutate([
              {
                notif_id: RELEASE_NOTIF_ID,
                channels: {
                  database: values.database,
                  email: values.email,
                },
              },
            ]);
          }}
        >
          <p className="mb-24 text-sm text-muted">
            <Trans message="Choose how you want to receive release notifications for movies and series you follow." />
          </p>

          <FormSwitch
            className="mb-24"
            name="database"
            description={
              <Trans message="Show release notifications inside the site notification bell." />
            }
          >
            <Trans message="Site notifications" />
          </FormSwitch>

          <FormSwitch
            name="email"
            description={
              <Trans message="Also send release notifications to my email address, if email notifications are enabled by the site administrator." />
            }
          >
            <Trans message="Email notifications" />
          </FormSwitch>
        </Form>
      )}
    </AccountSettingsPanel>
  );
}